import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import apiClient from "../api/client";

const API = window.location.origin;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    let mounted = true;

    async function run() {
      if (!token || token.length < 20) {
        setStatus("error");
        setError("Missing or invalid token.");
        return;
      }

      try {
        await apiClient.get(`/auth/verify-email`, { params: { token } });
        if (!mounted) return;
        setStatus("ok");
      } catch (e) {
        if (!mounted) return;
        setStatus("error");
        setError(e?.response?.data?.detail || "Verification failed. Link may be expired.");
      }
    }

    run();
    return () => { mounted = false; };
  }, [token]);

  // Auto-redirect countdown after success
  useEffect(() => {
    if (status !== "ok") return;
    if (countdown <= 0) {
      navigate("/auth?verified=1");
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown, navigate]);

  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white border border-slate-200 dark:bg-slate-900/60 dark:border-slate-800 p-6 shadow">
        <div className="text-center mb-4">
          <span className="text-4xl">💌</span>
          <h1 className="text-2xl font-bold mt-2 text-slate-900 dark:text-white">Email Verification</h1>
        </div>

        {status === "loading" && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 dark:border-white mx-auto mb-4"></div>
            <p className="text-slate-600 dark:text-slate-300">Verifying your email…</p>
          </div>
        )}

        {status === "ok" && (
          <div className="text-center py-6">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Email Verified!</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">Your email is verified. You can sign in now.</p>
            
            <Link
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition-colors"
              to="/auth?verified=1"
              data-testid="go-to-signin-btn"
            >
              Sign In Now
            </Link>
            
            <p className="text-slate-500 dark:text-slate-500 text-sm mt-4">
              Redirecting in {countdown}s...
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-6">
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-xl font-semibold text-rose-600 dark:text-red-300 mb-2">Verification Failed</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
            <div className="space-y-3">
              <Link 
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 font-semibold transition-colors" 
                to="/auth"
              >
                Back to Sign In
              </Link>
              <p className="text-slate-500 dark:text-slate-500 text-sm">
                Need a new verification link? Sign in to request one.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
